<template>
  <div class="error form">
    <div class="error__content">
      <template v-if="errorCode === '404'">
        <h1 class="jumbo-headline">404 Not Found</h1>
        <p>The page you are looking for does not exist.</p>
        <ButtonBase @onButtonClick="handleButtonClick">{{
          returnButtonLabel
        }}</ButtonBase>
      </template>

      <template v-else-if="errorCode === 'dontAllowDirectVisit'">
        <h1 class="jumbo-headline">Hello.</h1>
        <div v-html="errorMessage" />
      </template>
      
      <template v-else>
        <h3 class="small-headline">
          (Code: {{ errorCode || "Something went wrong" }})
        </h3>
        <h1 class="jumbo-headline">Oops!</h1>
        <p v-html="errorMessage || ''" />
        <ButtonBase @onButtonClick="handleButtonClick">{{
          returnButtonLabel
        }}</ButtonBase>
      </template>
    </div>
  </div>
</template>

<script setup>
import { useTitle } from '@vueuse/core';
import { useRouter } from 'vue-router';
import { ref, onMounted, watch } from 'vue';
import { storeToRefs } from 'pinia';

import ButtonBase from '@/components/ui/buttons/ButtonBase.vue';
import { useGlobalStore } from '@/stores/globalStore';
import { useErrorStore } from '@/stores/components/errorStore';

const errorStore = useErrorStore();

const errorCode = ref('');
const errorMessage = ref('');

const router = useRouter();
const globalStore = useGlobalStore();
const returnButtonLabel = ref('Create a new order');

// component expects props, not used though
const props = defineProps({
  errorCode: String,
  errorMessage: String,
});

const handleButtonClick = () => {
  const partnerReturnUrls = globalStore.getMerchantSettings?.returnPaymentUrls;
  const returnUrlOnFailure = partnerReturnUrls?.return_url_on_failure;
  const fallbackReturnUrl = globalStore.getMerchantSettings?.returnUrl;

  if (returnUrlOnFailure) {
    window.top.location = returnUrlOnFailure;
    return;
  }

  if (fallbackReturnUrl) {
    window.top.location = fallbackReturnUrl;
    return;
  }

  router.push('/');
};

watch(
  () => globalStore.getMerchantSettings,
  (val) => {
    if (val && val.returnMessage !== '') {
      returnButtonLabel.value = val.returnMessage;
    }
  },
  { deep: true, immediate: true },
);

onMounted(() => {
  errorCode.value = errorStore.error.code || props.errorCode;
  errorMessage.value = errorStore.error.message;
  if (errorCode.value !== 'dontAllowDirectVisit') {
    useTitle(`Banxa - Code: ${errorCode.value || 'Something went wrong!'}`);
  } else {
    useTitle('Something went wrong!');
  }
});
</script>
<style lang="scss" scoped>
.error {
  width: 100%;
  min-height: 25em;
  display: flex;
  justify-content: center;
  align-items: center;

  &__content {
    width: 100%;
    padding: 0 var(--padding-1) 2rem;
    max-width: 50rem;

    h3 {
      color: var(--primary);
      margin-bottom: 0.8rem;
    }

    h1 {
      margin-bottom: 1.6rem;
    }

    p {
      margin-bottom: 3.2rem;
    }
  }
}
</style>
